/**
 * बस्ती and crowded places of one booth, from the survey app (N-PAC, 28 Oct 2026: "हर बूथ में मलिन /
 * गरीब बस्ती, दलित बस्ती, आदिवासी बस्ती, घनी आबादी क्षेत्र — पता के साथ; सबसे ज़्यादा आबादी वाली
 * बस्ती अलग रंग में दिखे; सर्वे वाले मोबाइल ऐप में रखें"). Online: saved straight to the server. The
 * phone's location is taken by itself for a new place and can be taken again or removed.
 */
import { useEffect, useState } from "react";
import { apiErrorText } from "@core/messages";
import type { BoothPlace, BoothPlaceKind } from "../field/api";
import { useApp, useQuery, useSession } from "../ui/context";
import { Banner, Button, Field } from "../ui/components";

const KINDS: BoothPlaceKind[] = ["slum", "dalit", "tribal", "dense", "village", "market", "chowk", "crowd", "ground", "auditorium", "hall", "other"];
type Form = { id?: number; kind: BoothPlaceKind; name: string; address: string; population: string; households: string; lat: number | null; lng: number | null };
const blank = (): Form => ({ kind: "slum", name: "", address: "", population: "", households: "", lat: null, lng: null });
const num = (v: string) => (v.trim() === "" ? null : Number(v.replace(/[,\s]/g, "")));
const okNum = (v: string, max: number) => v.trim() === "" || (Number.isInteger(num(v)) && num(v)! >= 0 && num(v)! <= max);

export function PlacesScreen({ code }: { code: string }) {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();
  const booth = useQuery(async () => (assemblyId ? app.core.store.getBooth(assemblyId, code) : null), [assemblyId, code], ["booths"]);
  const [list, setList] = useState<{ items: BoothPlace[]; densestId: number | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tries, setTries] = useState(0);
  const [form, setForm] = useState<Form | null>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gps, setGps] = useState<"finding" | "taken" | "none" | null>(null);

  useEffect(() => {
    let off = false;
    app.online((api, aid) => api.boothPlaces(aid, code)).then(
      (x) => !off && (setList(x), setErr(null)),
      (e) => !off && setErr((e as { status?: number })?.status ? apiErrorText(e, lang) ?? String(e) : t.resNeedsNet)
    );
    return () => {
      off = true;
    };
  }, [app, code, lang, t, tries]);

  const locate = () => {
    if (!navigator.geolocation) return setGps("none");
    setGps("finding");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setForm((f) => (f ? { ...f, lat: Math.round(p.coords.latitude * 1e6) / 1e6, lng: Math.round(p.coords.longitude * 1e6) / 1e6 } : f));
        setGps("taken");
      },
      () => setGps("none"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  };
  const open = (f: Form) => {
    setForm(f);
    setTouched(false);
    setSaved(false);
    setGps(null);
    if (f.lat == null && !f.id) setTimeout(locate, 0);
  };

  // a place listed from the office without GPS (N-PAC, 1 Nov 2026): one tap on the spot takes this phone's
  // location and saves it, nothing else of the place changes
  const [locating, setLocating] = useState<number | null>(null);
  const takeHere = (p: BoothPlace) => {
    if (!navigator.geolocation) return setErr(t.bsGpsNone);
    setLocating(p.id);
    setErr(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const body = { kind: p.kind, name: p.name, address: p.address ?? null, population: p.population ?? null, households: p.households ?? null, lat: Math.round(pos.coords.latitude * 1e6) / 1e6, lng: Math.round(pos.coords.longitude * 1e6) / 1e6 };
        try {
          await app.online((api, aid) => api.saveBoothPlace(aid, code, body, p.id));
          setTries((n) => n + 1);
        } catch (e) {
          setErr((e as { status?: number })?.status ? apiErrorText(e, lang) ?? String(e) : t.resNeedsNet);
        } finally {
          setLocating(null);
        }
      },
      () => {
        setLocating(null);
        setErr(t.bsGpsNone);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  };

  const errs = form ? { name: !form.name.trim(), population: !okNum(form.population, 1_000_000), households: !okNum(form.households, 200_000) } : null;
  const save = async () => {
    if (!form || !errs) return;
    setTouched(true);
    if (Object.values(errs).some(Boolean)) return;
    setBusy(true);
    setErr(null);
    try {
      const body = { kind: form.kind, name: form.name.trim(), address: form.address.trim() || null, population: num(form.population), households: num(form.households), lat: form.lat, lng: form.lng };
      await app.online((api, aid) => api.saveBoothPlace(aid, code, body, form.id));
      setSaved(true);
      setForm(null);
      setTries((n) => n + 1);
    } catch (e) {
      setErr((e as { status?: number })?.status ? apiErrorText(e, lang) ?? String(e) : t.resNeedsNet);
    } finally {
      setBusy(false);
    }
  };

  const fmt = (n: number) => n.toLocaleString("en-IN");
  const items = list ? [...list.items].sort((a, b) => Number(b.id === list.densestId) - Number(a.id === list.densestId) || (b.population ?? (b.households ?? 0) * 5) - (a.population ?? (a.households ?? 0) * 5)) : [];

  return (
    <div className="stack" data-testid="places">
      <div>
        <div style={{ fontWeight: 800, color: "var(--primary)" }}>
          {t.boothNo} {code}
          {booth ? ` · ${booth.name}` : ""}
        </div>
        <h2 style={{ margin: "2px 0 4px" }}>🏘 {t.bsTitle}</h2>
        <p className="muted" style={{ margin: 0 }}>
          {t.bsHelp}
        </p>
      </div>
      {err ? <Banner kind="error" action={!list ? { label: t.reload, onClick: () => setTries((n) => n + 1) } : undefined}>{err}</Banner> : null}
      {saved ? <Banner kind="ok">{t.bsSaved}</Banner> : null}

      {form && errs ? (
        <form
          className="card stack bs-form"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div className="label">{t.bsKind}</div>
          <div className="bs-kinds" role="radiogroup" aria-label={t.bsKind}>
            {KINDS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={form.kind === k} className={`bs-kind k-${k}${form.kind === k ? " on" : ""}`} onClick={() => setForm({ ...form, kind: k })}>
                {t.bsKinds[k]}
              </button>
            ))}
          </div>
          <Field label={t.bsName} name="bs-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} maxLength={160} placeholder={t.bsNamePh} error={touched && errs.name ? t.required : null} />
          <Field label={t.bsAddress} name="bs-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} maxLength={300} placeholder={t.bsAddressPh} />
          <div className="bs-two">
            <Field label={t.bsPopulation} name="bs-pop" inputMode="numeric" value={form.population} onChange={(v) => setForm({ ...form, population: v })} placeholder="1200" error={touched && errs.population ? t.bsNumber : null} />
            <Field label={t.bsHouseholds} name="bs-hh" inputMode="numeric" value={form.households} onChange={(v) => setForm({ ...form, households: v })} placeholder="200" error={touched && errs.households ? t.bsNumber : null} />
          </div>
          <div>
            <div className="label">{t.bsGps}</div>
            <div className="bs-gps">
              <span>
                {gps === "finding" ? t.bsGpsFinding : form.lat != null ? `${gps === "taken" ? t.bsGpsAuto : "📍"} ${form.lat}, ${form.lng}` : gps === "none" ? t.bsGpsNone : "—"}
              </span>
              <Button variant="secondary" onClick={locate} disabled={gps === "finding"}>
                {t.bsGpsTake}
              </Button>
              {form.lat != null ? (
                <Button variant="secondary" onClick={() => setForm({ ...form, lat: null, lng: null })}>
                  {t.bsGpsClear}
                </Button>
              ) : null}
            </div>
          </div>
          <Button type="submit" big busy={busy} testId="bs-save">
            {t.bsSave}
          </Button>
          <Button variant="secondary" onClick={() => setForm(null)}>
            {t.bsCancel}
          </Button>
        </form>
      ) : list ? (
        <Button big onClick={() => open(blank())} testId="bs-add">
          {t.bsAdd}
        </Button>
      ) : null}

      {list && !items.length ? <p className="muted">{t.bsNone}</p> : null}
      {items.map((p) => {
        const top = p.id === list?.densestId;
        return (
          <div key={p.id} className={`card bs-item k-${p.kind}${top ? " top" : ""}`} data-testid="bs-item">
            <div className="card-head">
              <span className={`bs-chip k-${p.kind}`}>{t.bsKinds[p.kind] ?? p.kind}</span>
              {top ? <span className="bs-top">{t.bsDensest}</span> : null}
            </div>
            <h3 style={{ margin: "4px 0 2px" }}>{p.name}</h3>
            <div className="muted">
              {[p.population != null ? t.bsPeople(fmt(p.population)) : null, p.households != null ? t.bsHouses(fmt(p.households)) : null].filter(Boolean).join(" · ")}
            </div>
            {p.address ? <div style={{ fontSize: 15 }}>{p.address}</div> : null}
            <div className="bs-row">
              {p.lat != null ? (
                <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer">
                  📍 {t.bsGps}
                </a>
              ) : (
                <Button onClick={() => takeHere(p)} disabled={locating != null} testId={`bs-take-${p.id}`}>
                  {locating === p.id ? t.bsGpsFinding : t.bsGpsTake}
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() =>
                  open({ id: p.id, kind: p.kind, name: p.name, address: p.address ?? "", population: p.population != null ? String(p.population) : "", households: p.households != null ? String(p.households) : "", lat: p.lat, lng: p.lng })
                }
              >
                {t.bsEdit}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
